import React, { useState, useEffect } from 'react';
import { Product, ProductGroup, ModalActionType, ProductType } from '../types';
import { MOCK_PRODUCT_DATA, MOCK_PRODUCT_GROUPS_DATA, PlusCircleIcon } from '../constants';
import ProductTable from './ProductTable';
import ProductFormModal from './ProductFormModal';
import DeleteProductModal from './DeleteProductModal';
import ProductDefectsManager from './ProductDefectsManager';
import GroupTable from './GroupTable';
import GroupFormModal from './GroupFormModal';
import DeleteGroupModal from './DeleteGroupModal';
import HelpIcon from './HelpIcon';
import { useDatabaseContext } from '../contexts/DatabaseContext';

// Migration helper for old ProductGroup data
const migrateProductGroupData = (data: any[]): ProductGroup[] => {
  try {
    if (!Array.isArray(data)) return MOCK_PRODUCT_GROUPS_DATA;
    
    return data.map(group => {
      let migratedGroup = { ...group };
      
      // Migration: Rename old field names to new field names
      if (group.infeedName && !group.reportInfeedName) {
        migratedGroup.reportInfeedName = group.infeedName;
        delete migratedGroup.infeedName;
      }
      if (group.outfeedName && !group.reportOutfeedName) {
        migratedGroup.reportOutfeedName = group.outfeedName;
        delete migratedGroup.outfeedName;
      }
      // Remove infeedAverageWeight field if it exists
      if (group.infeedAverageWeight !== undefined) {
        delete migratedGroup.infeedAverageWeight;
      }
      
      // Ensure it has the right structure with all required fields
      return {
        ...migratedGroup,
        pocketAssignments: migratedGroup.pocketAssignments || [],
        outfeedProductIds: migratedGroup.outfeedProductIds || [],
        columnConfiguration: migratedGroup.columnConfiguration || []
      };
    });
  } catch (error) {
    console.error('Error migrating ProductGroup data:', error);
    return MOCK_PRODUCT_GROUPS_DATA;
  }
};

type ActiveTab = 'products' | 'groups';
type ProductTab = 'infeed' | 'outfeed';

const ProductsPage: React.FC = () => {
  const { apiUrl } = useDatabaseContext();
  
  // Products State - No localStorage!
  const [products, setProducts] = useState<Product[]>([]);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [productModalAction, setProductModalAction] = useState<ModalActionType | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isDefectsModalOpen, setIsDefectsModalOpen] = useState(false);
  const [defectsProduct, setDefectsProduct] = useState<Product | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);

  // Groups State
  const [productGroups, setProductGroups] = useState<ProductGroup[]>([]);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupModalAction, setGroupModalAction] = useState<ModalActionType | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<ProductGroup | null>(null);
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  
  const [activeTab, setActiveTab] = useState<ActiveTab>('groups');
  const [activeProductTab, setActiveProductTab] = useState<ProductTab>('infeed');

  // Load products from database on mount
  useEffect(() => {
    fetchProducts();
  }, [apiUrl]);

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      const response = await fetch(`${apiUrl}/products`);
      if (response.ok) {
        const data = await response.json();
        if (data.data && Array.isArray(data.data)) {
          setProducts(data.data);
        } else if (data.message === 'success' && Array.isArray(data.data)) {
          setProducts(data.data);
        }
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };
  
  // Load product groups from API on mount
  useEffect(() => {
    const fetchProductGroups = async () => {
      try {
        const response = await fetch(`${apiUrl}/product-groups`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.data && Array.isArray(data.data)) {
            // Fetch pocket assignments for each group
            const groupsWithPockets = await Promise.all(data.data.map(async (group) => {
              try {
                const pocketResponse = await fetch(`${apiUrl}/product-groups/${group.id}/pocket-assignments`);
                if (pocketResponse.ok) {
                  const pocketData = await pocketResponse.json();
                  return {
                    ...group,
                    pocketAssignments: pocketData.data || []
                  };
                }
              } catch (err) {
                console.error('Error fetching pocket assignments for group', group.id, err);
              }
              return {
                ...group,
                pocketAssignments: []
              };
            }));
            
            const migratedData = migrateProductGroupData(groupsWithPockets);
            setProductGroups(migratedData);
          }
        }
      } catch (error) {
        console.error('Error fetching product groups:', error);
      }
    };
    
    fetchProductGroups();
  }, [apiUrl]);

  // Product Modal Handlers
  const handleOpenProductModal = (action: ModalActionType, product?: Product) => {
    setProductModalAction(action);
    setSelectedProduct(product || null);
    setIsProductModalOpen(true);
  };

  const handleCloseProductModal = () => {
    setIsProductModalOpen(false);
    setProductModalAction(null);
    setSelectedProduct(null);
  };

  const handleAddOrModifyProduct = async (productData: Omit<Product, 'id'> | Product, isNew: boolean) => {
    try {
      if (isNew) {
        // Create new product in database
        const response = await fetch(`${apiUrl}/products`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });
        
        if (response.ok) {
          const result = await response.json();
          const newProduct = result.data || result;
          setProducts(prev => [newProduct, ...prev]);
        }
      } else {
        // Update existing product in database
        const response = await fetch(`${apiUrl}/products/${(productData as Product).id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(productData)
        });
        
        if (response.ok) {
          const result = await response.json();
          const updatedProduct = result.data || result;
          setProducts(prev => prev.map(p => p.id === updatedProduct.id ? updatedProduct : p));
        }
      }
    } catch (error) {
      console.error('Error saving product:', error);
    }
    handleCloseProductModal();
  };

  const handleManageDefects = (product: Product) => {
    setDefectsProduct(product);
    setIsDefectsModalOpen(true);
  };

  const handleDeleteProduct = async () => {
    if (selectedProduct) {
      try {
        const response = await fetch(`${apiUrl}/products/${selectedProduct.id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          setProducts(prev => prev.filter(p => p.id !== selectedProduct.id));
          // Also remove from product groups
          setProductGroups(prevGroups => 
            prevGroups.map(group => ({
              ...group,
              infeedProductId: group.infeedProductId === selectedProduct.id ? '' : group.infeedProductId,
              pocketAssignments: group.pocketAssignments.filter(pocket => pocket.productId !== selectedProduct.id),
            }))
          );
        }
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
    handleCloseProductModal();
  };
  
  const filteredProducts = products.filter(prod => {
    const matchesSearch = prod.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
      prod.productId.toLowerCase().includes(productSearchTerm.toLowerCase());
    const matchesProductType = activeProductTab === 'infeed' ? 
      prod.productType === ProductType.INFEED : 
      prod.productType === ProductType.OUTFEED;
    return matchesSearch && matchesProductType;
  });
  const existingProductIds = products.map(p => p.productId);

  // Group Modal Handlers
  const handleOpenGroupModal = (action: ModalActionType, group?: ProductGroup) => {
    setGroupModalAction(action);
    setSelectedGroup(group || null);
    setIsGroupModalOpen(true);
  };

  const handleCloseGroupModal = () => {
    setIsGroupModalOpen(false);
    setGroupModalAction(null);
    setSelectedGroup(null);
  };

  const handleAddOrModifyGroup = async (groupData: Omit<ProductGroup, 'id'> | ProductGroup, isNew: boolean) => {
    try {
      if (isNew) {
        // Create new product group
        const newGroup = {...(groupData as Omit<ProductGroup, 'id'>), id: `grp-${Date.now()}` };
        
        const response = await fetch(`${apiUrl}/product-groups`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(newGroup)
        });
        
        if (response.ok) {
          // Save pocket assignments if any
          if (newGroup.pocketAssignments && newGroup.pocketAssignments.length > 0) {
            await fetch(`${apiUrl}/product-groups/${newGroup.id}/pocket-assignments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ pocketAssignments: newGroup.pocketAssignments })
            });
          }
          
          setProductGroups(prev => [newGroup, ...prev]);
          handleCloseGroupModal();
        } else {
          const errorData = await response.json();
          alert(`Failed to create product group: ${errorData.error || 'Unknown error'}`);
        }
      } else {
        // Update existing group
        const updatedGroup = groupData as ProductGroup;
        
        const response = await fetch(`${apiUrl}/product-groups/${updatedGroup.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedGroup)
        });
        
        if (response.ok) {
          // Save pocket assignments
          if (updatedGroup.pocketAssignments) {
            await fetch(`${apiUrl}/product-groups/${updatedGroup.id}/pocket-assignments`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ pocketAssignments: updatedGroup.pocketAssignments })
            });
          }
          
          setProductGroups(prev => prev.map(g => g.id === updatedGroup.id ? updatedGroup : g));
          handleCloseGroupModal();
        } else {
          const errorData = await response.json();
          alert(`Failed to update product group: ${errorData.error || 'Unknown error'}`);
        }
      }
    } catch (error) {
      console.error('Error saving product group:', error);
      alert(`Error saving product group: ${error.message || 'Unknown error'}`);
    }
  };

  const handleDeleteGroup = async () => {
    if (selectedGroup) {
      try {
        const response = await fetch(`${apiUrl}/product-groups/${selectedGroup.id}`, {
          method: 'DELETE'
        });
        
        if (response.ok) {
          setProductGroups(prev => prev.filter(g => g.id !== selectedGroup.id));
        }
      } catch (error) {
        console.error('Error deleting product group:', error);
      }
    }
    handleCloseGroupModal();
  };

  const filteredGroups = productGroups.filter(group =>
    group.groupName.toLowerCase().includes(groupSearchTerm.toLowerCase()) ||
    group.infeedProductName?.toLowerCase().includes(groupSearchTerm.toLowerCase())
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center gap-2 mb-6">
        <h1 className="text-3xl font-bold text-slate-800">Products Management</h1>
        <HelpIcon section="products/product-types" tooltip="Learn about product management" />
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-4 mb-6 border-b">
        <button
          onClick={() => setActiveTab('groups')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'groups'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Product Groups
        </button>
        <button
          onClick={() => setActiveTab('products')}
          className={`px-4 py-2 font-medium transition-all ${
            activeTab === 'products'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-600 hover:text-slate-800'
          }`}
        >
          Products
        </button>
      </div>

      {/* Groups Tab */}
      {activeTab === 'groups' && (
        <div>
          <div className="mb-4 flex justify-between items-center">
            <input
              type="text"
              placeholder="Search groups..."
              value={groupSearchTerm}
              onChange={(e) => setGroupSearchTerm(e.target.value)}
              className="px-4 py-2 border rounded-lg w-64"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenGroupModal(ModalActionType.ADD)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                Add Product Group
              </button>
              <HelpIcon section="products/product-groups" tooltip="Learn about product groups" />
            </div>
          </div>
          
          <GroupTable
            groups={filteredGroups}
            onModify={(group) => handleOpenGroupModal(ModalActionType.MODIFY, group)}
            onDelete={(group) => handleOpenGroupModal(ModalActionType.DELETE, group)}
          />
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div>
          {/* Sub-tabs for Infeed/Outfeed */}
          <div className="flex space-x-2 mb-4">
            <button
              onClick={() => setActiveProductTab('infeed')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeProductTab === 'infeed'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Infeed Products
            </button>
            <button
              onClick={() => setActiveProductTab('outfeed')}
              className={`px-4 py-2 rounded-lg font-medium ${
                activeProductTab === 'outfeed'
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Outfeed Products
            </button>
          </div>

          <div className="mb-4 flex justify-between items-center">
            <input
              type="text"
              placeholder={`Search ${activeProductTab} products...`}
              value={productSearchTerm}
              onChange={(e) => setProductSearchTerm(e.target.value)}
              className="px-4 py-2 border rounded-lg w-64"
            />
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleOpenProductModal(ModalActionType.ADD)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center"
              >
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                Add Product
              </button>
              <HelpIcon section="products/product-types" tooltip="Learn about products" />
            </div>
          </div>
          
          {loadingProducts ? (
            <div className="text-center py-8">Loading products...</div>
          ) : (
            <ProductTable
              products={filteredProducts}
              onModify={(prod) => handleOpenProductModal(ModalActionType.MODIFY, prod)}
              onDelete={(prod) => handleOpenProductModal(ModalActionType.DELETE, prod)}
              onManageDefects={handleManageDefects}
            />
          )}
        </div>
      )}

      {/* Product Modals */}
      {productModalAction === ModalActionType.ADD && (
        <ProductFormModal
          isOpen={isProductModalOpen}
          onClose={handleCloseProductModal}
          onSubmit={handleAddOrModifyProduct}
          title="Add New Product"
          existingProductIds={existingProductIds}
          defaultProductType={activeProductTab === 'infeed' ? ProductType.INFEED : ProductType.OUTFEED}
        />
      )}
      {productModalAction === ModalActionType.MODIFY && selectedProduct && (
        <ProductFormModal
          isOpen={isProductModalOpen}
          onClose={handleCloseProductModal}
          onSubmit={handleAddOrModifyProduct}
          initialData={selectedProduct}
          title="Edit Product"
          existingProductIds={existingProductIds}
        />
      )}
      {productModalAction === ModalActionType.DELETE && selectedProduct && (
        <DeleteProductModal
          isOpen={isProductModalOpen}
          onClose={handleCloseProductModal}
          onConfirm={handleDeleteProduct}
          product={selectedProduct}
        />
      )}

      {/* Group Modals */}
      {groupModalAction === ModalActionType.ADD && (
        <GroupFormModal
          isOpen={isGroupModalOpen}
          onClose={handleCloseGroupModal}
          onSubmit={handleAddOrModifyGroup}
          title="Add New Product Group"
          allProducts={products}
          existingGroupIds={productGroups.map(g => g.groupId)}
        />
      )}
      {groupModalAction === ModalActionType.MODIFY && selectedGroup && (
        <GroupFormModal
          isOpen={isGroupModalOpen}
          onClose={handleCloseGroupModal}
          onSubmit={handleAddOrModifyGroup}
          initialData={selectedGroup}
          title="Edit Product Group"
          allProducts={products}
          existingGroupIds={productGroups.map(g => g.groupId)}
        />
      )}
      {groupModalAction === ModalActionType.DELETE && selectedGroup && (
        <DeleteGroupModal
          isOpen={isGroupModalOpen}
          onClose={handleCloseGroupModal}
          onConfirm={handleDeleteGroup}
          group={selectedGroup}
        />
      )}
      {isDefectsModalOpen && defectsProduct && (
        <ProductDefectsManager
          productId={defectsProduct.id}
          productName={defectsProduct.name}
          onClose={() => {
            setIsDefectsModalOpen(false);
            setDefectsProduct(null);
          }}
        />
      )}
    </div>
  );
};

export default ProductsPage;